import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useInView } from 'framer-motion';
import Lottie from 'lottie-react';
import hero2Animation from '../assets/hero2.json'; // Hero2 Lottie for A.I Insights
import Header from '../components/Header';
import Footer from '../components/Footer';

// Animation variants for Header
const headerVariants = {
  hidden: { opacity: 0, y: -20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, ease: 'easeOut', type: 'spring', stiffness: 100 },
  },
};

// Animation variants for A.I Insights Lottie (Hero2)
const aiInsightsLottieVariants = {
  hidden: { opacity: 0, x: -30 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, ease: 'easeOut', type: 'spring', stiffness: 90 },
  },
};

// Animation variants for A.I Insights Content
const aiInsightsContentVariants = {
  hidden: { opacity: 0, x: 30 },
  visible: (delay = 0) => ({
    opacity: 1,
    x: 0,
    transition: { duration: 0.6, delay, ease: 'easeOut', type: 'spring', stiffness: 90 },
  }),
};

// Animation variants for Sections
const sectionVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: (delay = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.5, delay, ease: 'easeOut', type: 'spring', stiffness: 90 },
  }),
};

// Animation variants for Cards
const cardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.9 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.6, delay: i * 0.15, ease: 'easeOut', type: 'spring', stiffness: 120 },
  }),
};

// Hover animation for buttons
const hoverVariants = {
  hover: {
    scale: 1.05,
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.2)',
    transition: { duration: 0.3, ease: 'easeOut' },
  },
};

const Services = () => {
  // Refs for scroll-based animations
  const aiInsightsRef = useRef(null);
  const whoItsForRef = useRef(null);
  const aiHireRef = useRef(null);
  const customAiRef = useRef(null);

  // Use useInView to detect when sections are in view
  const aiInsightsInView = useInView(aiInsightsRef, { margin: '-150px' });
  const whoItsForInView = useInView(whoItsForRef, { margin: '-150px' });
  const aiHireInView = useInView(aiHireRef, { margin: '-150px' });
  const customAiInView = useInView(customAiRef, { margin: '-150px' });

  return (
    <div className="flex flex-col min-h-screen font-sans text-black bg-white">
      {/* Global styles for glow and body */}
      <style>
        {`
          body { background-color: #ffffff; margin: 0; }
          .glow-button {
            animation: glow 1.5s ease-in-out infinite alternate;
          }
          @keyframes glow {
            from {
              box-shadow: 0 0 5px rgba(37, 150, 190, 0.4), 0 0 10px rgba(37, 150, 190, 0.3);
            }
            to {
              box-shadow: 0 0 15px rgba(37, 150, 190, 0.8), 0 0 25px rgba(37, 150, 190, 0.5);
            }
          }
        `}
      </style>

      {/* Header */}
      <motion.div
        initial="hidden"
        animate="visible"
        variants={headerVariants}
      >
        <Header />
      </motion.div>

      {/* A.I Insights Section */}
      <div className="w-full bg-gray-100">
        <div
          ref={aiInsightsRef}
          className="h-[70vh] flex flex-col md:flex-row items-center max-w-6xl mx-auto px-6 py-6"
        >
          {/* Left: Hero2 Lottie Animation */}
          <motion.div
            className="md:w-1/2 flex justify-center mt-6 md:mt-0"
            initial="hidden"
            animate={aiInsightsInView ? 'visible' : 'hidden'}
            variants={aiInsightsLottieVariants}
          >
            <Lottie
              animationData={hero2Animation}
              loop={true}
              className="w-full max-w-sm"
            />
          </motion.div>

          {/* Right: A.I Insights Content */}
          <motion.div
            className="md:w-1/2 text-center md:text-left"
            initial="hidden"
            animate={aiInsightsInView ? 'visible' : 'hidden'}
            variants={aiInsightsContentVariants}
            custom={0.4}
          >
            <h2 className="text-3xl font-bold text-[#2596be] mb-6">
              A.I Insights
            </h2>
            <p className="text-lg text-gray-900 mb-4">
              Your 24/7 AI expert.
            </p>
            <p className="text-base text-gray-900 mb-8 max-w-3xl md:mx-0 mx-auto">
              We keep up with AI—so you and your business don’t have to. Get personalized reports, strategic recommendations, and clear action plans—tailored just for your business, delivered automatically.
            </p>
            <div className="flex justify-center md:justify-start">
              <motion.div whileHover="hover" variants={hoverVariants}>
                <Link
                  to="/ai-insights"
                  className="bg-[#2596be] hover:bg-[#1e7a9b] text-white font-bold py-3 px-8 rounded-lg transition duration-200 glow-button"
                >
                  Learn More
                </Link>
              </motion.div>
            </div>
          </motion.div>
        </div>
      </div>

      {/* Who It's For Section */}
      <div className="w-full bg-white">
        <div ref={whoItsForRef} className="max-w-6xl mx-auto px-6 py-16">
          <motion.h2
            className="text-3xl font-bold text-[#2596be] mb-6 text-center"
            initial="hidden"
            animate={whoItsForInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0}
          >
            Who It's For
          </motion.h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg"
              initial="hidden"
              animate={whoItsForInView ? 'visible' : 'hidden'}
              variants={cardVariants}
              custom={0}
            >
              <h3 className="text-xl font-bold text-[#375290] mb-3">Small-to-Medium Businesses (SMEs)</h3>
              <p className="text-gray-600">
                For companies looking for accessible AI solutions with no overhead.
              </p>
            </motion.div>
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg"
              initial="hidden"
              animate={whoItsForInView ? 'visible' : 'hidden'}
              variants={cardVariants}
              custom={1}
            >
              <h3 className="text-xl font-bold text-[#375290] mb-3">Startups</h3>
              <p className="text-gray-600">
                Cost-effective, scalable AI strategies for fast growth.
              </p>
            </motion.div>
            <motion.div
              className="bg-white p-6 rounded-lg shadow-md hover:shadow-lg"
              initial="hidden"
              animate={whoItsForInView ? 'visible' : 'hidden'}
              variants={cardVariants}
              custom={2}
            >
              <h3 className="text-xl font-bold text-[#375290] mb-3">Large Enterprises</h3>
              <p className="text-gray-600">
                Strategic-level insights, industry benchmarks, and competitor tracking.
              </p>
            </motion.div>
          </div>
        </div>
      </div>

      {/* A.I Hire Section */}
      <div className="w-full bg-gray-100">
        <div ref={aiHireRef} className="max-w-6xl mx-auto px-6 py-16">
          <motion.h2
            className="text-3xl font-bold text-[#2596be] mb-6 text-center"
            initial="hidden"
            animate={aiHireInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0}
          >
            A.I Hire <span className="text-sm text-gray-600">(Coming Soon)</span>
          </motion.h2>
          <motion.p
            className="text-lg text-gray-900 mb-4 text-center"
            initial="hidden"
            animate={aiHireInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.2}
          >
            It’s time to hire your first AI employee.
          </motion.p>
          <motion.p
            className="text-base text-gray-900 mb-8 max-w-3xl mx-auto text-center"
            initial="hidden"
            animate={aiHireInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.4}
          >
            Access a growing library of specialized AI agents that take on real tasks—like screening resumes, writing emails, managing calendars, and updating your CRM. Think of it as hiring your next teammate—without the overhead.
          </motion.p>
        </div>
      </div>

      {/* Custom AI Projects Section */}
      <div className="w-full bg-white">
        <div ref={customAiRef} className="max-w-6xl mx-auto px-6 py-16">
          <motion.h2
            className="text-3xl font-bold text-[#2596be] mb-6 text-center"
            initial="hidden"
            animate={customAiInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0}
          >
            Custom AI Projects
          </motion.h2>
          <motion.p
            className="text-lg text-gray-900 mb-4 text-center"
            initial="hidden"
            animate={customAiInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.2}
          >
            AI solutions, built for your exact needs.
          </motion.p>
          <motion.p
            className="text-base text-gray-900 mb-8 max-w-3xl mx-auto text-center"
            initial="hidden"
            animate={customAiInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.4}
          >
            From predictive maintenance and fraud detection to content generation and recommendation engines, we develop machine learning systems that fit seamlessly into your business and industry goals.
          </motion.p>
          <motion.div
            className="flex justify-center"
            initial="hidden"
            animate={customAiInView ? 'visible' : 'hidden'}
            variants={sectionVariants}
            custom={0.6}
          >
            <motion.div whileHover="hover" variants={hoverVariants}>
              <Link
                to="/#"
                className="bg-[#2596be] hover:bg-[#1e7a9b] text-white font-bold py-3 px-8 rounded-lg transition duration-200 glow-button"
              >
                Learn More
              </Link>
            </motion.div>
          </motion.div>
        </div>
      </div>

      {/* Footer */}
      <Footer />
    </div>
  );
};

export default Services;